// Felhasználó felvétele és eltávolítása a webről.
//
// A FELVÉTEL SZERVEROLDALON TÖRTÉNIK, egy cloud functionben. Két okból:
//
// 1. A férőhely-keret betartatása. A megvásárolt helyek száma pénzügyi
//    korlát, a felületet viszont meg lehet kerülni; a Firestore szabálynyelve
//    pedig nem tud dokumentumokat számolni, tehát nem tudja megmondani, hány
//    tagja van már a cégnek. Marad a szerver.
//
// 2. Ezzel eltűnt egy kerülőút. Korábban a kliens egy MÁSODLAGOS Firebase-
//    appot indított, mert a `createUserWithEmailAndPassword` be is léptet az
//    új fiókkal — az admin különben kiesett volna a saját munkamenetéből. Az
//    Admin SDK nem léptet be senkit, tehát erre már nincs szükség.
//
// A JELSZÓ-BEÁLLÍTÓ LEVÉL viszont a kliensnél maradt: az Admin SDK csak
// LINKET tud generálni, levelet nem küld. A `sendPasswordResetEmail` küld, és
// — ellentétben a létrehozással — nem nyúl a hívó munkamenetéhez.

import { sendPasswordResetEmail, type Auth } from 'firebase/auth';
import { deleteDoc, doc, updateDoc, type Firestore } from 'firebase/firestore';
import { httpsCallable, type Functions } from 'firebase/functions';

export type NewUserInput = {
  email: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
  role: string;
  seatTier?: 'basic' | 'ai';
};

/// A kapcsolatokat KÍVÜLRŐL kapjuk, nem a `./firebase` globális példányaiból
/// — ugyanaz a minta, mint a feltöltési rétegben (`uploads.ts`).
export type UserAdminContext = {
  db: Firestore;
  auth: Auth;
  functions: Functions;
};

/// Új felhasználó felvétele a céghez.
///
/// A visszatérési érték az új felhasználó azonosítója — a hívónak szüksége
/// van rá, mert a csoporttagságot külön kell átvezetni (`syncUserGroups`).
///
/// A keret túllépésekor a szerver `resource-exhausted` hibát ad, benne a
/// pontos számokkal; azt a hívó változtatás nélkül megmutathatja.
export async function createCompanyUser(
  ctx: UserAdminContext,
  companyId: string,
  input: NewUserInput,
): Promise<string> {
  const call = httpsCallable<
    { companyId: string } & NewUserInput,
    { userId?: string }
  >(ctx.functions, 'createUserInCompany');

  const email = input.email.trim();
  const result = await call({
    companyId,
    email,
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    jobTitle: input.jobTitle,
    role: input.role,
    seatTier: input.seatTier ?? 'basic',
  });

  const userId = result.data?.userId;
  if (typeof userId !== 'string' || userId.length === 0) {
    throw new Error('A szerver nem adott vissza felhasználói azonosítót.');
  }

  // A levél küldése SZÁNDÉKOSAN a fiók létrejötte UTÁN van: ha a levél nem
  // megy ki, a felhasználó már létezik, és az admin újraküldheti a
  // szerkesztőből. Fordított sorrendben olyan fiókhoz küldenénk levelet, ami
  // nem jött létre. Ezért a hibát sem engedjük felbukni: a felvétel SIKERES.
  try {
    await sendPasswordResetEmail(ctx.auth, email);
  } catch (error) {
    console.warn('[BlazeData] A jelszó-beállító levél kiküldése nem sikerült.', error);
  }

  return userId;
}

/// A hely-szint módosítása.
///
/// Szintén szerveroldalon, mert az AI-s helyre váltás is fogyaszt keretet: a
/// létszám nem nő tőle, az AI-s helyek száma viszont igen.
export async function setSeatTier(
  ctx: UserAdminContext,
  companyId: string,
  userId: string,
  seatTier: 'basic' | 'ai',
): Promise<void> {
  const call = httpsCallable<
    { companyId: string; userId: string; seatTier: string },
    { seatTier?: string }
  >(ctx.functions, 'setUserSeatTier');
  await call({ companyId, userId, seatTier });
}

/// A felhasználó eltávolítása.
///
/// Azt teszi, amit a mobil (`users_repo.dart` `removeUser`): törli a
/// felhasználó dokumentumát. A BELÉPÉSI FIÓK ettől megmarad — annak
/// megszüntetéséhez admin jogosultság kell, amivel a kliens nem rendelkezik.
/// Ezt a felület ki is írja, nem hallgatjuk el.
///
/// Amit a mobil NEM csinál, mi viszont igen: a csoportokból is kivesszük.
/// Enélkül a törölt ember neve ott maradna a csoportok taglistáján, és a
/// kiosztásnál egy nem létező felelős lenne választható.
export async function deleteCompanyUser(
  ctx: UserAdminContext,
  companyId: string,
  userId: string,
  groups: { id: string; userIds: string[] }[],
): Promise<void> {
  for (const group of groups) {
    if (!group.userIds.includes(userId)) continue;
    await updateDoc(doc(ctx.db, 'projects', companyId, 'groups', group.id), {
      userIds: group.userIds.filter((id) => id !== userId),
    });
  }
  await deleteDoc(doc(ctx.db, 'users', userId));
}
